import { useQuery } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Queries } from '@shared/api';
import {
  useControlsActions,
  useControlsState,
  useGroupContext,
  useViewTemplateState,
} from '@shared/components/filter/state';
import {
  getFilterScalarKey,
  getFilterValueType,
} from '@shared/components/filter/state/filterScalarKey';
import {
  ApiFilterByComponent,
  FilterDateOperator,
  FilterDateSelection,
  FilterNumberSelection,
  FilterPersonSelection,
  FilterSelectionState,
  FilterSelectionStateGroup,
  FilterTagSelection,
  FilterTextSelection,
  SortOption,
  SortSelectionState,
  isFilterBooleanSelection,
  isFilterComponentMatchSelection,
} from '@shared/components/filter/types';
import type { Entity } from '@shared/data/entity';
import { useCardUpdatesActions } from '@shared/scenes/cardUpdates/CardUpdatesContext';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { useProfile } from '@shared/util/hooks';
import { notNil } from '@shared/util/notNil';
import { usePermission } from '@shared/util/permissions';
import { CardEntity } from './CardEntity';
import { useLoadMore } from './hooks/useLoadMore';
import { CARDS_PER_GROUP_PAGE, CARDS_PER_PAGE, useUpdateList } from './hooks/useUpdateList';
import { isRelatedEntityLimitError } from './isRelatedEntityLimitError';

export interface CardsEntityServiceProps {
  workflowTemplateId: string;
  viewTemplateId: string;
  workspaceId: number;
  pageSize?: number;
  dateComponentId?: string;
  // for tree views, the related-card component defining the hierarchy; requests
  // per-card child counts alongside the entities and scopes the query to one
  // level of the tree (see treeParentCardId)
  treeParentComponentId?: string | null;
  // for tree views, the card whose children to fetch; when unset, fetches root
  // cards (those with no parent value)
  treeParentCardId?: string;
}

export interface CardsEntityService {
  entities: Entity[];
  entityVariables: API.ListWorkflowEntitiesQueryVariables;
  totalCardCount: number;
  // per-card child counts, keyed by card id; sparse — cards with zero children
  // are omitted. Only populated when treeParentComponentId is set.
  childCardCounts: Map<string, number>;
  isCardsLoading: boolean;
  isMoreCardsLoading: boolean;
  canUpdateCard: boolean;
  onSearch(keyword: string): void;
  onReorder(componentId: string, order: string): void;
  fetchMore(): void;
  refetch(): void;
  hasMoreCards: boolean;
  errors: Error[];
}

export function useCardService({
  workflowTemplateId,
  viewTemplateId,
  workspaceId,
  pageSize,
  dateComponentId,
  treeParentComponentId,
  treeParentCardId,
}: CardsEntityServiceProps): CardsEntityService {
  const { sorts, filters, availableSorts, search } = useControlsState();
  const group = useGroupContext();
  const { viewTemplate } = useViewTemplateState();

  return useCardServiceQuery({
    workflowTemplateId,
    viewTemplateId,
    workspaceId,
    pageSize,
    sorts,
    filters,
    availableSorts,
    search,
    group,
    viewTemplate,
    dateComponentId,
    treeParentComponentId,
    treeParentCardId,
  });
}

export interface CardServiceQueryProps extends CardsEntityServiceProps {
  sorts: Map<string, SortSelectionState>;
  filters: Map<string, FilterSelectionState>;
  availableSorts: SortOption[];
  search: string | null;
  group: FilterSelectionStateGroup | null;
  viewTemplate: API.ViewTemplateFragment | undefined;
  dateComponentId?: string;
}

export function useCardServiceQuery({
  workflowTemplateId,
  viewTemplateId,
  workspaceId,
  pageSize,
  sorts: selectedSortOptions,
  filters: selectedFilterOptions,
  availableSorts,
  search,
  group,
  viewTemplate,
  dateComponentId,
  treeParentComponentId,
  treeParentCardId,
}: CardServiceQueryProps): CardsEntityService {
  const isGroup = !isNil(group);
  const canUpdateCard = usePermission({
    action: 'update',
    subject: 'Card',
    channelId: workspaceId,
  }).enabled;

  const sortByComponents = useMemo(() => {
    return Array.from(selectedSortOptions.values()).map(getSortComponent);
  }, [selectedSortOptions]);

  const filterByComponents = useMemo(() => {
    const selected = new Map(selectedFilterOptions);
    if (isGroup) {
      Array.from(group.entries()).forEach(([id, filter]) => {
        selected.set(id, filter);
      });
    }

    const converted = Array.from(selected.values())
      .map(v => getFilterComponent({ ...v, prepareForMutation: false }))
      .filter(notNil);
    // tree views scope each query to one level of the hierarchy: children of
    // treeParentCardId, or root cards (no parent value) when it is unset
    if (!isNil(treeParentComponentId)) {
      converted.push({
        componentId: treeParentComponentId,
        scalarKey: null,
        value: {
          ...EMPTY_FILTER_ONE_OF,
          containsString: isNil(treeParentCardId)
            ? null
            : { values: [treeParentCardId], negate: false },
          isEmpty: isNil(treeParentCardId) ? { value: true } : null,
        },
      });
    }
    return converted;
  }, [selectedFilterOptions, group, isGroup, treeParentComponentId, treeParentCardId]);

  const entityVariables: API.ListWorkflowEntitiesQueryVariables = useMemo(() => {
    const sortByCreatedAt: API.SortByComponent = {
      componentId: 'createdAt',
      direction: 'DESC',
    };

    const hasCreatedAtSort = availableSorts.some(item => item.id === 'createdAt');
    const customSortingEnabled = !!viewTemplate?.customSortingEnabled;
    const shouldUseCreatedAtAsDefaultSort = hasCreatedAtSort && !customSortingEnabled;
    const defaultSort = shouldUseCreatedAtAsDefaultSort ? [sortByCreatedAt] : [];

    const userSort = !isEmpty(sortByComponents) ? sortByComponents : defaultSort;

    // for calendar views, add the view's date component id as the primary sort
    // so that cards appear in the right order when navigating left/right in the
    // card modal
    const dateComponentSort: API.SortByComponent[] = dateComponentId
      ? [{ componentId: dateComponentId, direction: 'ASC' }]
      : [];

    const defaultPageSize = isGroup ? CARDS_PER_GROUP_PAGE : CARDS_PER_PAGE;

    // added conditionally so that non-tree query variables (and thus Apollo
    // cache keys) are unchanged
    const inverseCountsInput = treeParentComponentId
      ? { inverseRelatedCardCountsForComponentId: treeParentComponentId }
      : {};

    return {
      input: {
        workflowTemplateId,
        viewTemplateId,
        limit: pageSize ?? defaultPageSize,
        cursor: '',
        sortByComponents: [...dateComponentSort, ...userSort],
        filterByComponents,
        omniSearchQuery: search,
        ...inverseCountsInput,
      },
    };
  }, [
    availableSorts,
    dateComponentId,
    filterByComponents,
    isGroup,
    pageSize,
    search,
    sortByComponents,
    treeParentComponentId,
    workflowTemplateId,
    viewTemplateId,
    viewTemplate?.customSortingEnabled,
  ]);

  const {
    data,
    previousData,
    error,
    loading: isEntitiesLoading,
    fetchMore: entityFetchMore,
    refetch,
  } = useQuery<API.ListWorkflowEntitiesQuery, API.ListWorkflowEntitiesQueryVariables>(
    Queries.workflow.listWorkflowEntities,
    {
      variables: entityVariables,
      skip: isNil(workflowTemplateId),
      fetchPolicy: 'cache-and-network',
      // After the initial network fetch, switch to cache-first so that
      // subscription-driven writeQuery calls don't each trigger a new
      // network request. The subscription keeps the cache up to date.
      nextFetchPolicy: 'cache-first',
    },
  );

  // A filter or sort edit changes the variables; until the new result arrives
  // Apollo has no data for them, so fall back to the last one to keep rows on
  // screen. Scoped by collection because `previousData` belongs to the hook and
  // survives navigating to another workflow or view template.
  const collectionKey = `${workflowTemplateId}:${viewTemplateId}:${treeParentCardId ?? ''}`;
  const loadedCollectionKey = useRef<string | null>(null);
  useEffect(() => {
    if (!isNil(data)) {
      loadedCollectionKey.current = collectionKey;
    }
  }, [data, collectionKey]);

  const listEntities =
    data ?? (loadedCollectionKey.current === collectionKey ? previousData : undefined);

  const { isLoadingMore, loadMore } = useLoadMore(entityFetchMore, listEntities, entityVariables);
  const hasMoreCards = listEntities?.list.hasMore ?? false;

  const fetchMoreCards = useDebouncedCallback(
    useCallback(() => {
      // mid-refetch `listEntities` is the previous result, so is its cursor
      if (isLoadingMore || !hasMoreCards || isEntitiesLoading) {
        return;
      }

      loadMore();
    }, [isLoadingMore, loadMore, hasMoreCards, isEntitiesLoading]),
    100,
  );

  // publishes this query's in-flight state so the collection controls can show a
  // single indicator covering every query the view runs
  const { setLoading, setRelatedEntityLimitError } = useControlsActions();
  const { setRefetch } = useCardUpdatesActions();
  const queryId = useId();
  useEffect(() => {
    setLoading(queryId, isEntitiesLoading || isLoadingMore);
    return () => setLoading(queryId, false);
  }, [setLoading, queryId, isEntitiesLoading, isLoadingMore]);

  // A cross-workflow filter that resolves too many ids is the one query failure the customer can
  // fix, so it is published to the controls rather than left as an empty list (sc-83210).
  const hasRelatedEntityLimitError = isRelatedEntityLimitError(error);
  useEffect(() => {
    setRelatedEntityLimitError(queryId, hasRelatedEntityLimitError);
    return () => setRelatedEntityLimitError(queryId, false);
  }, [setRelatedEntityLimitError, queryId, hasRelatedEntityLimitError]);

  const { replaceKnownCards } = useUpdateList(entityVariables, {
    queryId,
    renderedList: listEntities,
    isLoading: isEntitiesLoading,
  });

  // publishes this query's refetch so the collection can refetch every query it runs at once, when
  // the customer takes up the offer of updated cards
  const isSkipped = isNil(workflowTemplateId);
  useEffect(() => {
    if (isSkipped) {
      return;
    }
    setRefetch(queryId, () => void refetch().then(result => replaceKnownCards(result.data)));
    return () => setRefetch(queryId, null);
  }, [setRefetch, queryId, refetch, isSkipped, replaceKnownCards]);
  const { profile } = useProfile();

  const entities = useStableEntities({
    listEntities,
    workflowTemplateId,
    viewTemplate,
    currentUser: profile,
  });

  const totalCardCount = listEntities?.list.totalResults ?? 0;

  const childCardCounts = useMemo(() => {
    const counts = new Map<string, number>();
    listEntities?.list.inverseRelatedCardCounts?.forEach(({ cardId, count }) => {
      counts.set(cardId, count);
    });
    return counts;
  }, [listEntities]);

  return useMemo(() => {
    return {
      entities,
      entityVariables,
      totalCardCount,
      childCardCounts,
      hasMoreCards,
      fetchMore: fetchMoreCards,
      refetch,
      canUpdateCard,
      isCardsLoading: isEntitiesLoading && isNil(listEntities),
      isMoreCardsLoading: isLoadingMore,
      onSearch: _ => {},
      onReorder: (_, __) => {},
      errors: [],
    };
  }, [
    entities,
    entityVariables,
    totalCardCount,
    childCardCounts,
    fetchMoreCards,
    canUpdateCard,
    hasMoreCards,
    isEntitiesLoading,
    isLoadingMore,
    listEntities,
    refetch,
  ]);
}

type GetFilterComponentProps = FilterSelectionState & { prepareForMutation?: boolean };

/**
 * The value of a filter on a single component. It leaves `matchesRelatedEntity` unset rather than
 * null: the innermost level of a saved cross-workflow filter doesn't select it, so leaving it out
 * keeps a round trip through filter state equal to the saved criteria.
 */
type LeafFilterValue = Omit<ApiFilterByComponent['value'], 'matchesRelatedEntity'>;
type LeafFilterByComponent = Omit<ApiFilterByComponent, 'value'> & { value: LeafFilterValue };

const EMPTY_LEAF_FILTER_ONE_OF: LeafFilterValue = {
  stringWildcard: null,
  containsNumber: null,
  containsString: null,
  dateIs: null,
  dateRange: null,
  dateTokenIs: null,
  numberIs: null,
  isEmpty: null,
  containsCurrentUser: null,
  booleanIs: null,
};

/**
 * A filter value with every one-of branch nulled out. Spread it and set the one branch a filter
 * uses.
 */
export const EMPTY_FILTER_ONE_OF: API.FilterByComponentFragment['value'] = {
  ...EMPTY_LEAF_FILTER_ONE_OF,
  matchesRelatedEntity: null,
};

/**
 * Builds the `FilterByComponent` sent to the API for a single filter selection.
 *
 * `scalarKey` is attached here rather than inside each per-type branch below: it is a property of
 * the component being filtered (see `getFilterScalarKey`) and never varies with the value picked,
 * so resolving it once keeps those branches concerned only with the `FilterOneOf` value. A
 * cross-workflow filter names two components and so resolves one for each - see below.
 */
export function getFilterComponent(props: GetFilterComponentProps): ApiFilterByComponent | null {
  if (isFilterComponentMatchSelection(props.selection)) {
    const matchFilter = getComponentMatchFilterValue(props);
    return isNil(matchFilter) ? null : toApiFilterByComponent(matchFilter);
  }
  const componentFilter = getComponentFilterValue(props);
  if (isNil(componentFilter)) {
    return null;
  }
  const inner = toApiFilterByComponent({
    ...componentFilter,
    scalarKey: getFilterScalarKey(props.filter),
  });
  const relatedEntityPath = props.filter?.relatedEntityPath;
  if (isNil(relatedEntityPath) || isEmpty(relatedEntityPath)) {
    return inner;
  }
  // A cross-workflow filter: the filter we just built targets a component on the *innermost*
  // workflow, so it moves inside `matchesRelatedEntity` and each hop above it wraps the level
  // below, innermost hop first. `type` is left unset, like every other variant here - the GraphQL
  // input default supplies it.
  //
  // Every level carries its own `scalarKey`, each resolved from the component that level names.
  // A hop is always a RELATED_CARD - that is what being in the path means - so it resolves the
  // same key a filter on that component directly would, which is how the traversal reaches the
  // same index.
  return relatedEntityPath.reduceRight<API.FilterByComponentFragment>(
    (nested, hop) => ({
      componentId: hop.componentId,
      scalarKey: getFilterScalarKey({ type: 'RELATED_CARD' }),
      value: {
        ...EMPTY_FILTER_ONE_OF,
        matchesRelatedEntity: { filterByComponent: nested },
      },
    }),
    inner,
  );
}

function toApiFilterByComponent(filter: LeafFilterByComponent): ApiFilterByComponent {
  return filter as ApiFilterByComponent;
}

/**
 * Builds the filter for a row that defers to a field on the card being edited (see
 * `FilterComponentMatchSelection`). The value names that field rather than anything to compare
 * against; it is resolved against the card at query time (see `useFilterByComponents`).
 *
 * Returns nothing for a row that names no field yet, and for one reached through a related card:
 * `matchesRelatedEntity` nests a plain `FilterByComponent`, whose value union has no dynamic
 * branch to put this in. The `Matches` operator isn't offered on such a row, so this is a guard
 * rather than a case that arises.
 */
function getComponentMatchFilterValue({
  filter,
  selection,
}: GetFilterComponentProps): LeafFilterByComponent | null {
  if (
    isNil(filter) ||
    !isFilterComponentMatchSelection(selection) ||
    isNil(selection.componentId) ||
    !isEmpty(filter.relatedEntityPath)
  ) {
    return null;
  }
  return {
    componentId: filter.id,
    scalarKey: getFilterScalarKey(filter),
    value: {
      ...EMPTY_LEAF_FILTER_ONE_OF,
      containsCurrentWorkflowTemplateComponentValue: { componentId: selection.componentId },
    },
  };
}

function getComponentFilterValue({
  filter,
  selection,
  prepareForMutation = true,
  negate = false,
}: GetFilterComponentProps): Omit<LeafFilterByComponent, 'scalarKey'> | null {
  if (isNil(filter)) {
    return null;
  }
  // The component's own type, unless a scalar representation reads as something else - see
  // `getFilterValueType`. Which `FilterOneOf` branch to build follows the value being compared,
  // not the component it came from.
  switch (getFilterValueType(filter)) {
    case 'BOOLEAN': {
      if (!isFilterBooleanSelection(selection)) {
        return null;
      }
      return {
        componentId: filter.id,
        value: {
          ...EMPTY_LEAF_FILTER_ONE_OF,
          // Always equality: a yes/no filter has no other comparison to make, so no operator is
          // surfaced (see `BooleanSelections`).
          booleanIs: { operator: 'EQ', value: selection.value },
        },
      };
    }
    case 'TAG':
    case 'RELATED_CARD': {
      const filterValues = (selection as FilterTagSelection[])
        .filter(s => s.value !== 'empty' && s.value !== 'notEmpty')
        .filter(s => !isNil(s.value))
        .map(s => s.value);
      let containsString: NonNullable<
        API.FilterByComponentFragment['value']['containsString']
      > | null = null;
      if (!isEmpty(filterValues)) {
        containsString = {
          values: filterValues,
          negate,
        };
      }
      const emptyValue = !isNil((selection as FilterTagSelection[]).find(s => s.value === 'empty'));
      const notEmptyValue = !isNil(
        (selection as FilterTagSelection[]).find(s => s.value === 'notEmpty'),
      );
      return {
        componentId: filter.id,
        value: {
          ...EMPTY_LEAF_FILTER_ONE_OF,
          containsString,
          isEmpty: getIsEmptyFilter(emptyValue, notEmptyValue),
        },
      };
    }
    case 'PERSON': {
      const emptyValue = !isNil(
        (selection as FilterPersonSelection[]).find(s => s.value === 'empty'),
      );
      const notEmptyValue = !isNil(
        (selection as FilterPersonSelection[]).find(s => s.value === 'notEmpty'),
      );
      const currentUser = !isNil(
        (selection as FilterPersonSelection[]).find(s => s.value === 'currentUser'),
      );
      const filterValues =
        !emptyValue && !notEmptyValue && !currentUser
          ? (selection as FilterPersonSelection[])
              .filter(
                s => s.value !== 'empty' && s.value !== 'notEmpty' && s.value !== 'currentUser',
              )
              .map(s => Number(s.value))
          : null;
      let containsNumber: NonNullable<
        API.FilterByComponentFragment['value']['containsNumber']
      > | null = null;
      if (!isNil(filterValues) && !isEmpty(filterValues)) {
        containsNumber = {
          values: filterValues,
          negate,
        };
      }
      return {
        componentId: filter.id,
        value: {
          ...EMPTY_LEAF_FILTER_ONE_OF,
          containsNumber,
          isEmpty: getIsEmptyFilter(emptyValue, notEmptyValue),
          containsCurrentUser: currentUser ? { value: currentUser } : null,
        },
      };
    }
    case 'TEXT': {
      const textOperator = (selection as FilterTextSelection)?.operator;
      const isSelectionEmpty = textOperator === 'empty';
      const isSelectionNotEmpty = textOperator === 'notEmpty';
      return {
        componentId: filter.id,
        value: {
          ...EMPTY_LEAF_FILTER_ONE_OF,
          stringWildcard:
            isSelectionEmpty || isSelectionNotEmpty
              ? null
              : {
                  value: (selection as FilterTextSelection).value ?? '',
                },
          isEmpty: getIsEmptyFilter(isSelectionEmpty, isSelectionNotEmpty),
        },
      };
    }
    case 'DATE': {
      const dateSelection = selection as FilterDateSelection;
      const isToday = dateSelection?.operator === 'today';
      const isRelative = isRelativeDateSelection(dateSelection);
      let dateRange: NonNullable<API.FilterByComponentFragment['value']['dateRange']> | null = null;

      if (!isRelative && operatorsThatNeedDates.includes(dateSelection?.operator)) {
        dateRange = getFilterDateRange(dateSelection);
      }

      const todayDateProps = getTodayDateFilters({
        hasTodayToken: isToday,
        convertToDateRange: !prepareForMutation,
      });

      // `duration` is already signed (negative = before today, positive =
      // after today), so it is passed through unchanged.
      const relativeDateProps = getRelativeDateFilters({
        isRelative,
        comparisonOperator: dateSelection?.comparisonOperator,
        duration: dateSelection?.duration,
        convertToDateRange: !prepareForMutation,
      });

      return {
        componentId: filter.id,
        value: {
          ...EMPTY_LEAF_FILTER_ONE_OF,
          dateRange,
          ...todayDateProps,
          ...relativeDateProps,
          isEmpty: getIsEmptyFilter(
            dateSelection?.operator === 'empty',
            dateSelection?.operator === 'notEmpty',
          ),
        },
      };
    }
    case 'NUMBER': {
      const operator = (selection as FilterNumberSelection).operator;
      return {
        componentId: filter.id,
        value: {
          ...EMPTY_LEAF_FILTER_ONE_OF,
          numberIs:
            operator !== 'empty' && operator !== 'notEmpty'
              ? {
                  operator,
                  value: (selection as FilterNumberSelection).value,
                }
              : null,
          isEmpty: getIsEmptyFilter(operator === 'empty', operator === 'notEmpty'),
        },
      };
    }
    default: {
      return null;
    }
  }
}

/**
 * The `isEmpty` branch, which reads both ways: `true` matches cards with no value for the
 * component, `false` cards with any value at all.
 */
function getIsEmptyFilter(
  isEmptySelected: boolean,
  isNotEmptySelected: boolean,
): API.FilterByComponentFragment['value']['isEmpty'] {
  if (isEmptySelected) {
    return { value: true };
  }
  if (isNotEmptySelected) {
    return { value: false };
  }
  return null;
}

const operatorsThatNeedDates: FilterDateOperator[] = ['exactly', 'between', 'startsOn', 'endsOn'];
const operatorsThatNeedStartDate: FilterDateOperator[] = ['exactly', 'between', 'startsOn'];
const operatorsThatNeedEndDate: FilterDateOperator[] = ['between', 'endsOn'];

// `startsOn`/`endsOn` become relative when "keep relative to today" is on,
// persisting as a single relative bound (`dateIs.relativeDate`).
function isRelativeDateSelection(selection: FilterDateSelection | undefined): boolean {
  return (
    !!selection?.isRelative &&
    (selection.operator === 'startsOn' || selection.operator === 'endsOn')
  );
}

function getFilterDateRange(
  dateSelection: FilterDateSelection,
): NonNullable<API.FilterByComponentFragment['value']['dateRange']> | null {
  if (operatorsThatNeedDates.includes(dateSelection?.operator)) {
    return {
      after: operatorsThatNeedStartDate.includes(dateSelection.operator)
        ? (dateSelection.after as string)
        : null,
      before: getFilterDateRangeEnd(dateSelection),
    };
  } else {
    return null;
  }
}

function getFilterDateRangeEnd(dateSelection: FilterDateSelection): string | null {
  if (operatorsThatNeedEndDate.includes(dateSelection.operator)) {
    return dateSelection.before ?? null;
  }
  if (dateSelection.operator === 'exactly') {
    return moment(dateSelection.after).endOf('day').toISOString() as string;
  }
  return null;
}

export function getTodayDateFilters({
  hasTodayToken,
  convertToDateRange,
}: {
  hasTodayToken: boolean;
  convertToDateRange: boolean;
}): {
  dateRange?: NonNullable<API.FilterByComponentFragment['value']['dateRange']>;
  dateTokenIs: NonNullable<API.FilterByComponentFragment['value']['dateTokenIs']> | null;
} {
  if (hasTodayToken && convertToDateRange) {
    return {
      dateRange: {
        after: moment().startOf('day').toISOString(),
        before: moment().endOf('day').toISOString(),
      },
      dateTokenIs: null,
    };
  }
  if (hasTodayToken) {
    return {
      dateTokenIs: { value: 'TODAY' },
    };
  }
  return {
    dateTokenIs: null,
  };
}

export function getRelativeDateFilters({
  isRelative,
  comparisonOperator,
  duration,
  convertToDateRange,
}: {
  isRelative: boolean;
  comparisonOperator?: API.ComparisonOperator;
  duration?: number;
  convertToDateRange: boolean;
}): {
  dateRange?: NonNullable<API.FilterByComponentFragment['value']['dateRange']>;
  dateIs: NonNullable<API.FilterByComponentFragment['value']['dateIs']> | null;
} {
  const isValidRelativeDate = isRelative && !isNil(comparisonOperator) && !isNil(duration);

  /* Notes
  Specified day is (+/-) duration days from today

  GT/GTE - after
    - !E is exclusive. start of day after specified day.
    - E is inclusive. start of specified day.
  LT/LTE - before
    - !E is exclusive. end of day before specified day.
    - E is inclusive. end of specified day
  */
  if (isValidRelativeDate && convertToDateRange) {
    const specifiedDay = moment().add(duration, 'days');
    let after: string | null = null;
    let before: string | null = null;
    if (comparisonOperator === 'GT') {
      after = specifiedDay.add(1, 'day').startOf('day').toISOString();
    }
    if (comparisonOperator === 'GTE') {
      after = specifiedDay.startOf('day').toISOString();
    }
    if (comparisonOperator === 'LT') {
      before = specifiedDay.subtract(1, 'day').endOf('day').toISOString();
    }
    if (comparisonOperator === 'LTE') {
      before = specifiedDay.endOf('day').toISOString();
    }

    return {
      dateRange: {
        after,
        before,
      },
      dateIs: null,
    };
  }

  if (isValidRelativeDate) {
    return {
      dateIs: {
        operator: comparisonOperator,
        relativeDate: {
          duration,
          timeUnit: 'DAYS',
        },
      },
    };
  }

  return {
    dateIs: null,
  };
}

export function getSortComponent({ sort, direction }: SortSelectionState): API.SortByComponent {
  return {
    componentId: sort.id,
    direction,
  };
}

interface GetEntitiesProps {
  listEntities: API.ListWorkflowEntitiesQuery | undefined;
  workflowTemplateId: string;
  viewTemplate: API.ViewTemplateFragment | undefined;
  currentUser?: User;
}

type EntityRefs = NonNullable<NonNullable<API.ListWorkflowEntitiesQuery['list']>['refs']>;

interface StableEntityCacheEntry {
  source: API.WorkflowEntity;
  built: CardEntity;
}

interface StableEntitySharedInputs {
  workflowTemplateId: string;
  viewTemplate: API.ViewTemplateFragment;
  currentUser?: User;
  refs: EntityRefs;
}

/**
 * getEntities with per-row identity stability: a row whose underlying entity
 * object (and the inputs shared by every row) is unchanged gets back the same
 * CardEntity instance as the previous render. Any cache broadcast that touches
 * a list query produces a new result object, and rebuilding a CardEntity for
 * every row on each one hands every row component a new `entity` prop —
 * defeating their React.memo and turning a one-card update into a re-render of
 * the whole collection view. Safe because CardEntity instances are never
 * mutated after construction, and unchanged rows keep their object identity in
 * Apollo results (normalized entities + result caching with
 * assumeImmutableResults).
 */
export function useStableEntities({
  listEntities,
  workflowTemplateId,
  viewTemplate,
  currentUser,
}: GetEntitiesProps): Entity[] {
  const cacheRef = useRef(new Map<string, StableEntityCacheEntry>());
  const sharedInputsRef = useRef<StableEntitySharedInputs | null>(null);

  return useMemo(() => {
    const entityList = listEntities?.list.entities;
    const refs = listEntities?.list.refs;
    if (!entityList || !refs || !viewTemplate) {
      cacheRef.current.clear();
      sharedInputsRef.current = null;
      return [];
    }

    const previousShared = sharedInputsRef.current;
    const sharedInputsUnchanged =
      !isNil(previousShared) &&
      previousShared.workflowTemplateId === workflowTemplateId &&
      previousShared.viewTemplate === viewTemplate &&
      previousShared.currentUser === currentUser &&
      // the list update path rebuilds the refs arrays even when their items
      // are untouched, so equivalence is by item identity, not array identity
      areRefsEquivalent(previousShared.refs, refs);
    if (!sharedInputsUnchanged) {
      cacheRef.current.clear();
    }

    const previousCache = cacheRef.current;
    const nextCache = new Map<string, StableEntityCacheEntry>();
    const entities = entityList.filter(isEntity).map(entity => {
      const cached = previousCache.get(entity.id);
      const built =
        cached?.source === entity
          ? cached.built
          : new CardEntity({ entity, workflowTemplateId, refs, viewTemplate, currentUser });
      nextCache.set(entity.id, { source: entity, built });
      return built;
    });

    // dropping rows no longer in the list keeps the cache from growing across
    // filter and page changes
    cacheRef.current = nextCache;
    sharedInputsRef.current = { workflowTemplateId, viewTemplate, currentUser, refs };
    return entities;
  }, [listEntities, workflowTemplateId, viewTemplate, currentUser]);
}

function areRefsEquivalent(previous: EntityRefs, next: EntityRefs): boolean {
  if (previous === next) {
    return true;
  }
  return (
    areRefListsEqual(previous.personRefs, next.personRefs) &&
    areRefListsEqual(previous.workflowEntityRefs, next.workflowEntityRefs) &&
    areRefListsEqual(previous.workflowTemplateRefs, next.workflowTemplateRefs) &&
    areRefListsEqual(previous.recurringEntitiesScheduleRefs, next.recurringEntitiesScheduleRefs)
  );
}

function areRefListsEqual<RefItem>(
  previous: RefItem[] | null | undefined,
  next: RefItem[] | null | undefined,
): boolean {
  if (previous === next) {
    return true;
  }
  if (isNil(previous) || isNil(next) || previous.length !== next.length) {
    return false;
  }
  return previous.every((item, index) => item === next[index]);
}

export function getEntities({
  listEntities,
  workflowTemplateId,
  viewTemplate,
  currentUser,
}: GetEntitiesProps): Entity[] {
  const entityList = listEntities?.list.entities;
  const refs = listEntities?.list.refs;
  if (!entityList || !refs || !viewTemplate) {
    return [];
  }

  return entityList
    .filter(isEntity)
    .map(entity => new CardEntity({ entity, workflowTemplateId, refs, viewTemplate, currentUser }));
}

export function isEntity(
  entity: NonNullable<API.ListWorkflowEntitiesQuery['list']['entities']>[number] | null,
): entity is NonNullable<API.ListWorkflowEntitiesQuery['list']['entities']>[number] {
  return !isNil(entity);
}
